package com.evoapps.evoshift.ui.roster

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ChevronRight
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.AvailabilitySlot
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.schedule.AssignCandidate
import com.evoapps.evoshift.widgets.ErrorOnNavy
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.ui.util.label
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.DialogSection
import com.evoapps.evoshift.widgets.EvoSecondaryButton
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.busy_elsewhere_other_employer
import evoshift.composeapp.generated.resources.busy_elsewhere_same_owner
import evoshift.composeapp.generated.resources.cancel
import evoshift.composeapp.generated.resources.employee_from_other_workplaces
import evoshift.composeapp.generated.resources.roster_picker_away
import evoshift.composeapp.generated.resources.roster_picker_guest_from
import evoshift.composeapp.generated.resources.roster_picker_guests_hint
import evoshift.composeapp.generated.resources.roster_picker_absent
import evoshift.composeapp.generated.resources.roster_picker_month_hours
import evoshift.composeapp.generated.resources.roster_picker_month_hours_target
import evoshift.composeapp.generated.resources.roster_picker_nobody
import evoshift.composeapp.generated.resources.roster_picker_over_target
import evoshift.composeapp.generated.resources.roster_picker_same_day
import evoshift.composeapp.generated.resources.roster_picker_title
import org.jetbrains.compose.resources.stringResource

/** SH-167: test tag of the day sheet's person picker. */
const val DAY_ASSIGN_PICKER_TAG = "day_assign_picker"

/**
 * SH-167: what the day's details need to assign people in place: "Assign" on each unassigned
 * shift opens the people for it (in the same sheet or pane); one tap assigns and the day stays.
 */
class DayAssigning(
    /** The shift whose picker is open; null: the day's shifts show. */
    val pickerShiftId: String?,
    val candidates: (shiftId: String) -> List<AssignCandidate>,
    val availabilitySlots: List<AvailabilitySlot>,
    val onOpen: (Shift) -> Unit,
    val onPick: (userId: String) -> Unit,
    val onClose: () -> Unit,
    /** SH-267: "From your other workplaces": people of the group the managers may borrow for the shift. */
    val guests: (shiftId: String) -> List<AssignCandidate> = { emptyList() }
)

/** "Barista · 08:00–16:00" (or the times alone): the shift as the picker and its button name it. */
internal fun Shift.pickerLabel(): String =
    listOfNotNull(requiredRole?.takeIf { it.isNotBlank() }, plannedRange()).joinToString(" · ")

/** Whole hours stay whole ("96"), others keep one decimal ("7.5"). */
private fun pickerHours(hours: Double): String =
    if (hours % 1.0 == 0.0) hours.toInt().toString() else ((hours * 10).toInt() / 10.0).toString()

/**
 * SH-167: the people for [shift], best first (available, no answer, unavailable), each with their
 * answer for the day, their hours this month (and contracted hours, over them in amber) and what
 * stands in the way (absence, work elsewhere, another shift that day). A tap assigns.
 */
@Composable
internal fun DayAssignPicker(shift: Shift, assigning: DayAssigning) {
    val candidates = assigning.candidates(shift.id)
    val guests = assigning.guests(shift.id)
    Column(Modifier.fillMaxWidth().testTag(DAY_ASSIGN_PICKER_TAG), verticalArrangement = Arrangement.spacedBy(12.dp)) {
        DialogSection(stringResource(Res.string.roster_picker_title, shift.pickerLabel())) {
            if (candidates.isEmpty() && guests.isEmpty()) {
                Text(
                    stringResource(Res.string.roster_picker_nobody),
                    style = MaterialTheme.typography.bodyMedium,
                    color = Color.White.copy(alpha = 0.7f)
                )
            }
            candidates.forEach { candidate -> CandidateRow(candidate, assigning) }
        }
        // SH-267: people of the group's other workplaces, ranked the same way, each with where they come from.
        if (guests.isNotEmpty()) {
            DialogSection(stringResource(Res.string.employee_from_other_workplaces)) {
                Text(
                    stringResource(Res.string.roster_picker_guests_hint),
                    style = MaterialTheme.typography.labelSmall,
                    color = Color.White.copy(alpha = 0.75f)
                )
                guests.forEach { candidate -> CandidateRow(candidate, assigning) }
            }
        }
        EvoSecondaryButton(DialogAction(stringResource(Res.string.cancel), assigning.onClose), Modifier.fillMaxWidth())
    }
}

@Composable
private fun CandidateRow(candidate: AssignCandidate, assigning: DayAssigning) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(Color.White.copy(alpha = if (candidate.hasConflict) 0.03f else 0.08f))
            .handCursor()
            .clickable { assigning.onPick(candidate.user.uid) }
            .padding(horizontal = 16.dp, vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Text(candidate.user.displayName, style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold, color = Color.White)
            candidate.guestFrom?.let { from ->
                Text(stringResource(Res.string.roster_picker_guest_from, from.workplaceName), style = MaterialTheme.typography.labelSmall, color = LightGold)
            }
            val absence = candidate.absence
            if (candidate.awayAtHome) {
                Text(stringResource(Res.string.roster_picker_away), style = MaterialTheme.typography.labelSmall, color = ErrorOnNavy, fontWeight = FontWeight.Bold)
            } else if (absence != null) {
                Text(
                    stringResource(Res.string.roster_picker_absent, absence.type.label()),
                    style = MaterialTheme.typography.labelSmall,
                    color = ErrorOnNavy,
                    fontWeight = FontWeight.Bold
                )
            } else {
                AvailabilityLine(candidate.preference, assigning.availabilitySlots)
            }
            val month = pickerHours(candidate.monthHours)
            Text(
                candidate.targetHours?.let { stringResource(Res.string.roster_picker_month_hours_target, month, pickerHours(it)) }
                    ?: stringResource(Res.string.roster_picker_month_hours, month),
                style = MaterialTheme.typography.labelSmall,
                color = Color.White.copy(alpha = 0.75f)
            )
            if (candidate.overTarget) {
                Text(
                    stringResource(Res.string.roster_picker_over_target),
                    style = MaterialTheme.typography.labelSmall,
                    color = Color(0xFFFFB74D),
                    fontWeight = FontWeight.Bold
                )
            }
            candidate.busyElsewhere.forEach { busy ->
                val where = if (busy.sameOwner) {
                    "${stringResource(Res.string.busy_elsewhere_same_owner)} · ${listOfNotNull(busy.workplaceName, busyTimes(busy)).joinToString(" ")}"
                } else {
                    "${stringResource(Res.string.busy_elsewhere_other_employer)} · ${busyTimes(busy)}"
                }
                Text(where, style = MaterialTheme.typography.labelSmall, color = LightGold)
            }
            if (candidate.sameDayShifts.isNotEmpty()) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    if (candidate.overlaps) {
                        Icon(Icons.Default.Warning, contentDescription = null, tint = ErrorOnNavy, modifier = Modifier.size(12.dp))
                        Spacer(Modifier.width(4.dp))
                    }
                    Text(
                        stringResource(Res.string.roster_picker_same_day, candidate.sameDayShifts.joinToString(", ") { it.plannedRange() }),
                        style = MaterialTheme.typography.labelSmall,
                        color = if (candidate.overlaps) ErrorOnNavy else Color.White.copy(alpha = 0.75f),
                        fontWeight = if (candidate.overlaps) FontWeight.Bold else null
                    )
                }
            }
        }
        Icon(Icons.Default.ChevronRight, contentDescription = null, tint = Color.White.copy(alpha = 0.6f))
    }
}
